/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ProxyConf } from '../models/proxy-conf';
import { WebBuildDetailResponse } from '../models/web-build-detail-response';
import { WebBuildResponse } from '../models/web-build-response';

@Injectable({
  providedIn: 'root',
})
export class DevService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getProxyConfiguration
   */
  static readonly GetProxyConfigurationPath = '/api/dev/proxy-conf';

  /**
   * Obtain a proxy configuration for Angular development
   * using Bearer or Cookie authentication
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProxyConfiguration()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProxyConfiguration$Response(params?: {
    bearer?: true;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<ProxyConf>> {

    const rb = new RequestBuilder(this.rootUrl, DevService.GetProxyConfigurationPath, 'get');
    if (params) {
      rb.query('bearer', params.bearer, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ProxyConf>;
      })
    );
  }

  /**
   * Obtain a proxy configuration for Angular development
   * using Bearer or Cookie authentication
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProxyConfiguration$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProxyConfiguration(params?: {
    bearer?: true;
  },
  context?: HttpContext

): Observable<ProxyConf> {

    return this.getProxyConfiguration$Response(params,context).pipe(
      map((r: StrictHttpResponse<ProxyConf>) => r.body as ProxyConf)
    );
  }

  /**
   * Path part for operation getWebBuilds
   */
  static readonly GetWebBuildsPath = '/api/dev/webBuilds';

  /**
   * List all uploaded WebXR builds
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getWebBuilds()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWebBuilds$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<WebBuildResponse>>> {

    const rb = new RequestBuilder(this.rootUrl, DevService.GetWebBuildsPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WebBuildResponse>>;
      })
    );
  }

  /**
   * List all uploaded WebXR builds
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getWebBuilds$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWebBuilds(params?: {
  },
  context?: HttpContext

): Observable<Array<WebBuildResponse>> {

    return this.getWebBuilds$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<WebBuildResponse>>) => r.body as Array<WebBuildResponse>)
    );
  }

  /**
   * Path part for operation createWebBuild
   */
  static readonly CreateWebBuildPath = '/api/dev/webBuilds';

  /**
   * Upload a new WebXR build. If label is omitted, the build is uploaded as "default"
   * and served at /webxr. Otherwise it is served at /webxr/{label}.
   * Requires Admin or Developer role.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createWebBuild()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createWebBuild$Response(params: {

    /**
     * URL-safe slug (lowercase letters, digits, hyphens). Defaults to "default".
     */
    label?: string;
    body: {
'file': Blob;
}
  },
  context?: HttpContext

): Observable<StrictHttpResponse<WebBuildResponse>> {

    const rb = new RequestBuilder(this.rootUrl, DevService.CreateWebBuildPath, 'post');
    if (params) {
      rb.query('label', params.label, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WebBuildResponse>;
      })
    );
  }

  /**
   * Upload a new WebXR build. If label is omitted, the build is uploaded as "default"
   * and served at /webxr. Otherwise it is served at /webxr/{label}.
   * Requires Admin or Developer role.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createWebBuild$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createWebBuild(params: {

    /**
     * URL-safe slug (lowercase letters, digits, hyphens). Defaults to "default".
     */
    label?: string;
    body: {
'file': Blob;
}
  },
  context?: HttpContext

): Observable<WebBuildResponse> {

    return this.createWebBuild$Response(params,context).pipe(
      map((r: StrictHttpResponse<WebBuildResponse>) => r.body as WebBuildResponse)
    );
  }

  /**
   * Path part for operation getWebBuild
   */
  static readonly GetWebBuildPath = '/api/dev/webBuilds/{label}';

  /**
   * Get details for a specific WebXR build, including filesystem size
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getWebBuild()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWebBuild$Response(params: {

    /**
     * Build label
     */
    label: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<WebBuildDetailResponse>> {

    const rb = new RequestBuilder(this.rootUrl, DevService.GetWebBuildPath, 'get');
    if (params) {
      rb.path('label', params.label, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WebBuildDetailResponse>;
      })
    );
  }

  /**
   * Get details for a specific WebXR build, including filesystem size
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getWebBuild$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWebBuild(params: {

    /**
     * Build label
     */
    label: string;
  },
  context?: HttpContext

): Observable<WebBuildDetailResponse> {

    return this.getWebBuild$Response(params,context).pipe(
      map((r: StrictHttpResponse<WebBuildDetailResponse>) => r.body as WebBuildDetailResponse)
    );
  }

  /**
   * Path part for operation updateWebBuild
   */
  static readonly UpdateWebBuildPath = '/api/dev/webBuilds/{label}';

  /**
   * Replace the content of an existing WebXR build.
   * Requires Admin or Developer role.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateWebBuild()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  updateWebBuild$Response(params: {

    /**
     * Label of the build to replace
     */
    label: string;
    body: {
'file': Blob;
}
  },
  context?: HttpContext

): Observable<StrictHttpResponse<WebBuildResponse>> {

    const rb = new RequestBuilder(this.rootUrl, DevService.UpdateWebBuildPath, 'put');
    if (params) {
      rb.path('label', params.label, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WebBuildResponse>;
      })
    );
  }

  /**
   * Replace the content of an existing WebXR build.
   * Requires Admin or Developer role.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateWebBuild$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  updateWebBuild(params: {

    /**
     * Label of the build to replace
     */
    label: string;
    body: {
'file': Blob;
}
  },
  context?: HttpContext

): Observable<WebBuildResponse> {

    return this.updateWebBuild$Response(params,context).pipe(
      map((r: StrictHttpResponse<WebBuildResponse>) => r.body as WebBuildResponse)
    );
  }

  /**
   * Path part for operation deleteWebBuild
   */
  static readonly DeleteWebBuildPath = '/api/dev/webBuilds/{label}';

  /**
   * Delete a WebXR build and its files.
   * Requires Admin or Developer role.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteWebBuild()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteWebBuild$Response(params: {

    /**
     * Label of the build to delete
     */
    label: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, DevService.DeleteWebBuildPath, 'delete');
    if (params) {
      rb.path('label', params.label, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Delete a WebXR build and its files.
   * Requires Admin or Developer role.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteWebBuild$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteWebBuild(params: {

    /**
     * Label of the build to delete
     */
    label: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteWebBuild$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getScaffoldZip
   */
  static readonly GetScaffoldZipPath = '/api/dev/scaffold';

  /**
   * Generate a scaffolded Unity island project as a downloadable zip.
   * The zip includes setup.sh and setup.bat, which initialise git, LFS, and the template's submodules.
   * Pre-requisite: create the GitLab repo before running the setup script.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getScaffoldZip()` instead.
   *
   * This method doesn't expect any request body.
   */
  getScaffoldZip$Response(params: {

    /**
     * Island display name (e.g. "My Factory")
     */
    name: string;

    /**
     * Short description of the island
     */
    description: string;

    /**
     * Optional UUID for the island. Auto-generated if omitted.
     */
    projectId?: string;

    /**
     * Optional GitLab SSH remote URL. Embedded in setup.sh if provided.
     */
    remoteUrl?: string;

    /**
     * Optional API base URL for the island. Defaults to this server's HOST_URL.
     */
    apiUrl?: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, DevService.GetScaffoldZipPath, 'get');
    if (params) {
      rb.query('name', params.name, {});
      rb.query('description', params.description, {});
      rb.query('projectId', params.projectId, {});
      rb.query('remoteUrl', params.remoteUrl, {});
      rb.query('apiUrl', params.apiUrl, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Generate a scaffolded Unity island project as a downloadable zip.
   * The zip includes setup.sh and setup.bat, which initialise git, LFS, and the template's submodules.
   * Pre-requisite: create the GitLab repo before running the setup script.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getScaffoldZip$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getScaffoldZip(params: {

    /**
     * Island display name (e.g. "My Factory")
     */
    name: string;

    /**
     * Short description of the island
     */
    description: string;

    /**
     * Optional UUID for the island. Auto-generated if omitted.
     */
    projectId?: string;

    /**
     * Optional GitLab SSH remote URL. Embedded in setup.sh if provided.
     */
    remoteUrl?: string;

    /**
     * Optional API base URL for the island. Defaults to this server's HOST_URL.
     */
    apiUrl?: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.getScaffoldZip$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation uploadScaffoldTemplate
   */
  static readonly UploadScaffoldTemplatePath = '/api/dev/scaffold/template';

  /**
   * Upload a new island project template zip. Replaces the existing template.
   * Requires Admin or Developer role.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadScaffoldTemplate()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadScaffoldTemplate$Response(params: {
    body: {
'file': Blob;
}
  },
  context?: HttpContext

): Observable<StrictHttpResponse<{
'message': string;
}>> {

    const rb = new RequestBuilder(this.rootUrl, DevService.UploadScaffoldTemplatePath, 'put');
    if (params) {
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{
        'message': string;
        }>;
      })
    );
  }

  /**
   * Upload a new island project template zip. Replaces the existing template.
   * Requires Admin or Developer role.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadScaffoldTemplate$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadScaffoldTemplate(params: {
    body: {
'file': Blob;
}
  },
  context?: HttpContext

): Observable<{
'message': string;
}> {

    return this.uploadScaffoldTemplate$Response(params,context).pipe(
      map((r: StrictHttpResponse<{
'message': string;
}>) => r.body as {
'message': string;
})
    );
  }

}
